/**
 * Scoring Persistence Layer
 * 
 * Writes MCF values and aggregated scores to the database using upserts.
 * Handles the NULL entity_id case for overall scores via delete-then-insert.
 */

import { supabase } from '@/integrations/supabase/client';
import type { McfExtractionResult, AggregationResult } from './types';

export async function persistMcfValues(
  userId: string,
  teamId: string,
  mcfResults: McfExtractionResult[]
): Promise<void> {
  const now = new Date().toISOString();

  const rows = mcfResults
    .filter(r => r.score !== null)
    .map(r => ({
      user_id: userId,
      team_id: teamId,
      mcf_id: r.mcfId,
      calculated_value: {
        score: r.score,
        raw_answer: r.rawAnswer,
        source_question: r.sourceQuestionKey
      },
      calculation_method: r.calculationMethod,
      last_calculated_at: now
    }));

  for (const row of rows) {
    const { error } = await supabase
      .from('user_mcf_values')
      .upsert(row as any, { onConflict: 'user_id,team_id,mcf_id', ignoreDuplicates: false });

    if (error) console.error(`[Scoring] MCF persist failed for ${row.mcf_id}:`, error);
  }
}

export async function persistScores(
  userId: string,
  teamId: string,
  mcScores: Map<string, AggregationResult>,
  sectionScores: Map<string, AggregationResult>,
  pillarScores: Map<string, AggregationResult>,
  overallScore: AggregationResult
): Promise<void> {
  const now = new Date().toISOString();
  const version = '1.0';

  const buildRow = (entityType: string, entityId: string | null, result: AggregationResult, extra: Record<string, any> = {}) => ({
    user_id: userId,
    team_id: teamId,
    entity_type: entityType,
    entity_id: entityId,
    score_value: result.score,
    score_breakdown: {
      input_count: result.inputCount,
      total_possible: result.totalPossible,
      coverage_percent: result.coveragePercent,
      ...extra
    },
    contributing_factors: {},
    last_calculated_at: now,
    calculation_version: version
  });

  // Collect entity rows (mc, section, pillar)
  const entityRows: any[] = [];

  for (const [mcId, result] of mcScores) {
    entityRows.push(buildRow('mc', mcId, result));
  }
  for (const [sectionId, result] of sectionScores) {
    entityRows.push(buildRow('section', sectionId, result));
  }
  for (const [pillarId, result] of pillarScores) {
    entityRows.push(buildRow('pillar', pillarId, result, {
      sections_scored: result.inputCount,
      sections_total: result.totalPossible
    }));
  }

  // Upsert entity rows
  for (const row of entityRows) {
    const { error } = await supabase
      .from('user_scores')
      .upsert(row as any, { onConflict: 'user_id,team_id,entity_type,entity_id', ignoreDuplicates: false });

    if (error) console.error(`[Scoring] Score persist failed for ${row.entity_type}/${row.entity_id}:`, error);
  }

  // Overall score: delete-then-insert (NULL entity_id can't use upsert with partial index)
  await supabase
    .from('user_scores')
    .delete()
    .eq('user_id', userId)
    .eq('team_id', teamId)
    .eq('entity_type', 'overall')
    .is('entity_id', null);

  const overallRow = buildRow('overall', null, overallScore, {
    pillars_scored: overallScore.inputCount,
    is_preliminary: overallScore.inputCount < 5
  });

  const { error } = await supabase
    .from('user_scores')
    .insert(overallRow as any);

  if (error) console.error('[Scoring] Overall score persist failed:', error);
}
